<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, roundTo, emptyMongoId } from '@/utils/helpers'
import { newVegcoBucket, unitésParBoîte } from '@/utils/helpers-vegco'

const storeU = useUtilisateurStore()
const dialog = ref(false)
const modifications = ref(false)
const isLoading = ref(false)

const balisesChoix = ref([] as string[])

const bucketGauche = ref<MDBVegcoBucket>(newVegcoBucket())
const bucketDroite = ref<MDBVegcoBucket>(newVegcoBucket())

const splitQuantité = ref(0)
const quantitéGauche = ref(0)
const quantitéDroite = ref(0)
const quantitéSplitMax = ref(0)
const quantitéEnfantsGauche = ref(0)
const quantitéEnfantsDroite = ref(0)
const quantitéTotaleGauche = ref(0)
const quantitéTotaleDroite = ref(0)

const unitParBoîte = computed(() => unitésParBoîte(bucketGauche.value.culture))

const quantitéGaucheAffichage = computed(() => {
  return roundTo(quantitéGauche.value / unitParBoîte.value, 2) + ' / ' +
    roundTo(quantitéTotaleGauche.value / unitParBoîte.value, 2)
})
const quantitéDroiteAffichage = computed(() => {
  if (quantitéTotaleDroite.value == 0) {
    return roundTo(quantitéDroite.value / unitParBoîte.value, 2)
  }
  return roundTo(quantitéDroite.value / unitParBoîte.value, 2) + ' / ' +
    roundTo(quantitéTotaleDroite.value / unitParBoîte.value, 2)
})
const quantitéGaucheCombo = computed(() => roundTo(quantitéGauche.value / unitParBoîte.value / 25, 1))
const quantitéDroiteCombo = computed(() => roundTo(quantitéDroite.value / unitParBoîte.value / 25, 1))
const pourcentageSlider = computed(() => (splitQuantité.value / quantitéSplitMax.value) * 100)
const isError = computed(() => quantitéGauche.value < 0 || quantitéDroite.value < 0)

const sliderColor = computed(() => {
  if (isError.value) {
    return 'red'
  } else {
    return 'primary'
  }
})

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

watch(splitQuantité, (_) => recalculerLesQuantités())

function recalculerLesQuantités() {
  quantitéGauche.value = quantitéSplitMax.value - splitQuantité.value - quantitéEnfantsGauche.value
  quantitéDroite.value = splitQuantité.value - quantitéEnfantsDroite.value
}

async function openSplit(buckets: MDBVegcoBucket[]) {
  if (buckets.length == 0) {
    notifyError('Aucun bucket sélectionné')
    return
  }

  const masterBuckets = buckets.filter(b => b.bucketParentId == emptyMongoId())
  if (masterBuckets.length == 0) {
    notifyError('Aucun bucket parent trouvé')
    return
  }

  isLoading.value = true
  dialog.value = true
  modifications.value = false

  bucketGauche.value = masterBuckets[0]
  const bucketsEnfants0 = buckets.filter(b => b.bucketParentId == masterBuckets[0].id)
  quantitéEnfantsGauche.value = bucketsEnfants0.reduce((acc, b) => acc + b.quantité, 0)
  quantitéTotaleGauche.value = quantitéEnfantsGauche.value + masterBuckets[0].quantité

  if (masterBuckets[1] != undefined) {
    bucketDroite.value = masterBuckets[1]
    const bucketsEnfants1 = buckets.filter(b => b.bucketParentId == masterBuckets[1].id)
    quantitéEnfantsDroite.value = bucketsEnfants1.reduce((acc, b) => acc + b.quantité, 0)
    quantitéTotaleDroite.value = quantitéEnfantsDroite.value + masterBuckets[1].quantité
  } else {
    bucketDroite.value = newVegcoBucket()
    quantitéEnfantsDroite.value = 0
    quantitéTotaleDroite.value = 0
    bucketDroite.value.date = bucketGauche.value.date
    bucketDroite.value.balises = [...bucketGauche.value.balises]
  }

  quantitéGauche.value = bucketGauche.value.quantité
  quantitéDroite.value = bucketDroite.value.quantité
  quantitéSplitMax.value = quantitéTotaleGauche.value + quantitéTotaleDroite.value
  splitQuantité.value = quantitéDroite.value

  await storeU.fetchBalisesIfNeeded()
  balisesChoix.value = storeU.getBalises('Bucket', 'Culture', bucketGauche.value.culture,
    'Variété', bucketGauche.value.variété).map(b => b.nom)

  isLoading.value = false

  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

async function confirm() {
  try
  {
    if (isError.value) {
      notifyError('Quantité négative dans un des buckets')
      return
    }

    const siteCS = storeU.getSiteLettresDappel(bucketGauche.value.siteId)
    const semiId = bucketGauche.value.semiId

    const data = {
      bucketGaucheId: bucketGauche.value.id,
      bucketDroiteId: bucketDroite.value.id,
      quantitéGauche: quantitéGauche.value,
      quantitéDroite: quantitéDroite.value,
      prioritéGauche: bucketGauche.value.priorité,
      prioritéDroite: bucketDroite.value.priorité,
      dateGauche: bucketGauche.value.date,
      dateDroite: bucketDroite.value.date,
      commentairesGauche: bucketGauche.value.commentaires,
      commentairesDroite: bucketDroite.value.commentaires,
      balisesGauche: bucketGauche.value.balises,
      balisesDroite: bucketDroite.value.balises
    }

    await apiProphet.put(`vgco/sites/${siteCS}/semis/${semiId}/buckets/separer`, data)
    modifications.value = true
    dialog.value = false
  }
  catch (e) {
    notifyError(e)
  }

  dialog.value = false
}

function cancel() {
  dialog.value = false
}

defineExpose({ openSplit })
</script>

<template>
  <v-dialog v-model="dialog" max-width="900px" :fullscreen="$vuetify.display.smAndDown" :loading="isLoading">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5 text-medium-emphasis ps-2">Séparer le semi en 2 buckets</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-slider
          v-model="splitQuantité" :min="0" :max="quantitéSplitMax" :step="unitParBoîte"
          thumb-label="always" class="mt-5" :color="sliderColor">
          <template #thumb-label="">
            {{ roundTo(pourcentageSlider, 2) }}%
          </template>
        </v-slider>
        <v-row class="mb-4">
          <v-col>
            <v-card>
              <v-card-title>
                <span>Semi {{bucketGauche.numéroSemi}}</span>
              </v-card-title>
              <v-card-text class="pt-3">
                <v-text-field
                  v-model="quantitéGaucheAffichage" label="Quantité"
                  readonly density="compact" variant="outlined"/>
                <v-date-input
                  v-model="bucketGauche.date" label="Date récolte"
                  display-format="normalDateWithWeekday" prepend-icon=""/>
                <v-text-field v-model="bucketGauche.priorité" label="Priorité de base" type="number"/>
                <v-textarea v-model="bucketGauche.commentaires" rows="2" label="Commentaires"/>
                <v-select v-if="quantitéEnfantsGauche == 0" v-model="bucketGauche.balises" :items="balisesChoix" label="Balises" chips multiple/>
                <v-select v-else v-model="bucketGauche.balises" :items="balisesChoix" label="Balises" chips multiple readonly variant="outlined"/>
                <v-text-field
                  v-if="bucketGauche.balises.includes('Combo')" v-model="quantitéGaucheCombo" label="Combos"
                  readonly density="compact" variant="outlined"/>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="1" class="d-flex justify-center align-center">
            <v-icon icon="M6.45,17.45L1,12L6.45,6.55L7.86,7.96L4.83,11H19.17L16.14,7.96L17.55,6.55L23,12L17.55,17.45L16.14,16.04L19.17,13H4.83L7.86,16.04L6.45,17.45Z" size="40px" class="text-h5 text-medium-emphasis"/>
          </v-col>

          <v-col>
            <v-card>
              <v-card-title>
                <span v-if="bucketDroite.numéroSemi == ''">Nouveau bucket</span>
                <span v-else>Semi {{bucketDroite.numéroSemi}}</span>
              </v-card-title>
              <v-card-text class="pt-3">
                <v-text-field
                  v-model="quantitéDroiteAffichage" label="Quantité"
                  readonly density="compact" variant="outlined"/>
                <v-date-input
                  v-model="bucketDroite.date" label="Date récolte"
                  display-format="normalDateWithWeekday" prepend-icon=""/>
                <v-text-field v-model="bucketDroite.priorité" label="Priorité de base" type="number"/>
                <v-textarea v-model="bucketDroite.commentaires" rows="2" label="Commentaires"/>
                <v-select v-if="quantitéEnfantsDroite == 0" v-model="bucketDroite.balises" :items="balisesChoix" label="Balises" chips multiple/>
                <v-select v-else v-model="bucketDroite.balises" :items="balisesChoix" label="Balises" chips multiple readonly variant="outlined"/>
                <v-text-field
                  v-if="bucketDroite.balises.includes('Combo')" v-model="quantitéDroiteCombo" label="Combos"
                  readonly density="compact" variant="outlined"/>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="d-flex justify-end mr-2 my-2">
        <v-btn @click="cancel">Annuler</v-btn>
        <v-btn color="primary" variant="flat" @click="confirm">Séparer les buckets</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
